import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Trash2, Save, Image, Check, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const LOGO_VARIANTS = [
  { key: 'default', label: 'Default Logo', description: 'Used in header, favicon, PWA install icon & apple-touch-icon. Recommended: square 512 × 512 px, transparent PNG (larger source = sharper at every size).', color: 'blue' },
  { key: 'true', label: 'TRUE Verdict Logo', description: 'Displayed when verdict is TRUE/Verified. Recommended: square 512 × 512 px, transparent PNG (green-coded brand shield works best).', color: 'green' },
  { key: 'mixed', label: 'MIXED Verdict Logo', description: 'Displayed for PSEUDO/MIXED/Inconclusive/Partially True. Recommended: square 512 × 512 px, transparent PNG (amber-coded brand shield).', color: 'amber' },
  { key: 'false', label: 'FALSE Verdict Logo', description: 'Displayed when verdict is FALSE/Debunked/Misleading. Recommended: square 512 × 512 px, transparent PNG (red-coded brand shield).', color: 'red' },
  { key: 'badge_widget', label: 'Badge Widget Logo', description: 'Used in influencer badge widgets. Recommended: square 256 × 256 px, transparent PNG.', color: 'purple' },
  { key: 'og_share', label: 'Social Share Image (OG)', description: 'The preview image shown when links are shared on WhatsApp, LinkedIn, Facebook & X. Upload exactly 1200 × 630 px (JPG/PNG, max 5 MB). Smaller images may not render correctly on all platforms. Replaces the auto-generated orange image.', color: 'teal' },
];

const FONT_WEIGHTS = [
  { value: 'normal', label: 'Normal' },
  { value: 'medium', label: 'Medium' },
  { value: 'semibold', label: 'Semi Bold' },
  { value: 'bold', label: 'Bold' },
];

const AdminBrandSettingsPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [tagline, setTagline] = useState('');
  const [taglineStyle, setTaglineStyle] = useState({
    color_light: '#334155',
    color_dark: '#cbd5e1',
    font_weight: 'medium'
  });
  const [logos, setLogos] = useState({});
  const [uploadingVariant, setUploadingVariant] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    loadBrandSettings();
  }, []);

  const loadBrandSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.request('/brand-settings/admin');
      setTagline(response.tagline || 'KaiCheckIt - before you believe it!');
      setTaglineStyle(response.tagline_style || {
        color_light: '#334155',
        color_dark: '#cbd5e1',
        font_weight: 'medium'
      });
      setLogos(response.logos || {});
      setUpdatedAt(response.updated_at);
    } catch (error) {
      console.error('Error loading brand settings:', error);
      setMessage({ type: 'error', text: 'Failed to load brand settings' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTagline = async () => {
    setIsSaving(true);
    try {
      await apiService.request('/brand-settings/tagline', {
        method: 'PUT',
        body: JSON.stringify({ 
          tagline,
          color_light: taglineStyle.color_light,
          color_dark: taglineStyle.color_dark,
          font_weight: taglineStyle.font_weight
        }),
      });
      setMessage({ type: 'success', text: 'Tagline saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving tagline:', error);
      setMessage({ type: 'error', text: 'Failed to save tagline' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelect = (variant) => {
    fileInputRefs.current[variant]?.click();
  };

  const handleFileChange = async (variant, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please select an image file' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image must be less than 5MB' });
      return;
    }

    setUploadingVariant(variant);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand-settings/logo/${variant}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      setLogos(prev => ({
        ...prev,
        [variant]: data.url
      }));
      
      setMessage({ type: 'success', text: `${variant} logo uploaded successfully!` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: `Failed to upload ${variant} logo` });
    } finally {
      setUploadingVariant(null);
      // Reset file input
      if (fileInputRefs.current[variant]) {
        fileInputRefs.current[variant].value = '';
      }
    }
  };

  const handleDeleteLogo = async (variant) => {
    if (!window.confirm(`Are you sure you want to delete the ${variant} logo?`)) {
      return;
    }

    setUploadingVariant(variant);
    try {
      await apiService.request(`/brand-settings/logo/${variant}`, {
        method: 'DELETE',
      });
      
      setLogos(prev => {
        const updated = { ...prev };
        delete updated[variant];
        return updated;
      });
      
      setMessage({ type: 'success', text: `${variant} logo deleted` });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting logo:', error);
      setMessage({ type: 'error', text: `Failed to delete ${variant} logo` });
    } finally {
      setUploadingVariant(null);
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700',
      green: 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700',
      amber: 'bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700',
      red: 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700',
      purple: 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700',
      teal: 'bg-teal-100 dark:bg-teal-900/30 border-teal-300 dark:border-teal-700',
    };
    return colors[color] || colors.blue;
  };

  const getBadgeColor = (color) => {
    const colors = {
      blue: 'bg-blue-600',
      green: 'bg-green-600',
      amber: 'bg-amber-600',
      red: 'bg-red-600',
      purple: 'bg-purple-600',
      teal: 'bg-teal-600',
    };
    return colors[color] || colors.blue;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt - Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Page Title */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                Brand Settings
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Manage your app's logos and tagline
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadBrandSettings}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>

          {/* Message Alert */}
          {message && (
            <Alert className={message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' 
              : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
            }>
              {message.type === 'success' ? (
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              )}
              <AlertDescription className={message.type === 'success' 
                ? 'text-green-800 dark:text-green-200' 
                : 'text-red-800 dark:text-red-200'
              }>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          {/* Last Updated */}
          {updatedAt && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Last updated: {new Date(updatedAt).toLocaleString()}
            </p>
          )}

          {/* Tagline Section */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-800 dark:text-slate-100">Tagline</CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-400">
                This tagline appears across your app: homepage, share prompts, and more.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Tagline Text */}
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                  Tagline Text
                </Label>
                <Input
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="Enter your tagline..."
                  className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                  data-testid="tagline-input"
                />
              </div>

              {/* Style Controls */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                {/* Light Mode Color */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                    Light Mode Color
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={taglineStyle.color_light}
                      onChange={(e) => setTaglineStyle(prev => ({ ...prev, color_light: e.target.value }))}
                      className="w-10 h-10 rounded border border-slate-300 dark:border-slate-600 cursor-pointer"
                      data-testid="color-light-input"
                    />
                    <Input
                      value={taglineStyle.color_light}
                      onChange={(e) => setTaglineStyle(prev => ({ ...prev, color_light: e.target.value }))}
                      className="flex-1 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 font-mono text-sm"
                      placeholder="#334155"
                    />
                  </div>
                </div>

                {/* Dark Mode Color */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                    Dark Mode Color
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={taglineStyle.color_dark}
                      onChange={(e) => setTaglineStyle(prev => ({ ...prev, color_dark: e.target.value }))}
                      className="w-10 h-10 rounded border border-slate-300 dark:border-slate-600 cursor-pointer"
                      data-testid="color-dark-input"
                    />
                    <Input
                      value={taglineStyle.color_dark}
                      onChange={(e) => setTaglineStyle(prev => ({ ...prev, color_dark: e.target.value }))}
                      className="flex-1 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 font-mono text-sm"
                      placeholder="#cbd5e1"
                    />
                  </div>
                </div>

                {/* Font Weight */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                    Font Weight
                  </Label>
                  <Select
                    value={taglineStyle.font_weight}
                    onValueChange={(value) => setTaglineStyle(prev => ({ ...prev, font_weight: value }))}
                  >
                    <SelectTrigger className="dark:bg-slate-800 dark:border-slate-600" data-testid="font-weight-select">
                      <SelectValue placeholder="Select weight" />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_WEIGHTS.map((weight) => (
                        <SelectItem key={weight.value} value={weight.value}>
                          {weight.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Preview */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                  Preview
                </Label>
                <div className="flex gap-4">
                  <div className="flex-1 p-4 rounded-lg bg-white border border-slate-200">
                    <p className="text-xs text-slate-400 mb-1">Light Mode</p>
                    <p 
                      style={{ color: taglineStyle.color_light, fontWeight: taglineStyle.font_weight === 'normal' ? 400 : taglineStyle.font_weight === 'medium' ? 500 : taglineStyle.font_weight === 'semibold' ? 600 : 700 }}
                      className="text-lg"
                    >
                      {tagline}
                    </p>
                  </div>
                  <div className="flex-1 p-4 rounded-lg bg-slate-900 border border-slate-700">
                    <p className="text-xs text-slate-500 mb-1">Dark Mode</p>
                    <p 
                      style={{ color: taglineStyle.color_dark, fontWeight: taglineStyle.font_weight === 'normal' ? 400 : taglineStyle.font_weight === 'medium' ? 500 : taglineStyle.font_weight === 'semibold' ? 600 : 700 }}
                      className="text-lg"
                    >
                      {tagline}
                    </p>
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveTagline}
                  disabled={isSaving}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="save-tagline-btn"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Tagline
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Logo Variants Section */}
          <Card className="border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-slate-800 dark:text-slate-100">Logo Variants</CardTitle>
              <CardDescription className="text-slate-600 dark:text-slate-400">
                Upload different logo versions for various contexts. Recommended sizes vary by slot — see each slot below for exact dimensions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {LOGO_VARIANTS.map((variant) => (
                <div
                  key={variant.key}
                  className={`p-4 rounded-lg border-2 ${getColorClasses(variant.color)} transition-all`}
                  data-testid={`logo-variant-${variant.key}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Logo Preview */}
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {logos[variant.key] ? (
                        <img
                          src={logos[variant.key]}
                          alt={`${variant.label}`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <Image className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                      )}
                    </div>

                    {/* Info & Actions */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                          {variant.label}
                        </h3>
                        <Badge className={`${getBadgeColor(variant.color)} text-white text-xs`}>
                          {variant.key}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                        {variant.description}
                      </p>

                      {/* File Input (hidden) */}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={(el) => (fileInputRefs.current[variant.key] = el)}
                        onChange={(e) => handleFileChange(variant.key, e)}
                      />

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFileSelect(variant.key)}
                          disabled={uploadingVariant === variant.key}
                          className="text-slate-700 dark:text-slate-200"
                          data-testid={`upload-${variant.key}-btn`}
                        >
                          {uploadingVariant === variant.key ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {logos[variant.key] ? 'Replace' : 'Upload'}
                        </Button>
                        {logos[variant.key] && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLogo(variant.key)}
                            disabled={uploadingVariant === variant.key}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                            data-testid={`delete-${variant.key}-btn`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>

                      {/* Current URL */}
                      {logos[variant.key] && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 truncate">
                          URL: {logos[variant.key]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Usage Guide */}
          <Card className="border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg text-slate-800 dark:text-slate-100">Usage Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
              <p><strong className="text-slate-700 dark:text-slate-300">Default Logo:</strong> Appears in the app header, favicon, and general branding areas.</p>
              <p><strong className="text-slate-700 dark:text-slate-300">TRUE Logo:</strong> Shown on result cards when the verdict is "Fact" or "Verified".</p>
              <p><strong className="text-slate-700 dark:text-slate-300">MIXED Logo:</strong> Shown for "Pseudo Fact" or "Partially True" verdicts.</p>
              <p><strong className="text-slate-700 dark:text-slate-300">FALSE Logo:</strong> Shown when the verdict is "False" or "Misleading".</p>
              <p><strong className="text-slate-700 dark:text-slate-300">Badge Widget Logo:</strong> Used specifically in influencer certification badge widgets.</p>
              <p><strong className="text-slate-700 dark:text-slate-300">Social Share Image (OG):</strong> The exact image shown in link previews on WhatsApp, LinkedIn, Facebook & X. Must be 1200 x 630 px. If not uploaded, an auto-generated image with your Default Logo is used.</p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminBrandSettingsPage;
